import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConnectionError from "../../../components/ConnectionError/ConnectionError";
import EmptyState from "../../../components/EmptyState/EmptyState";
import { Modal } from "../../../components/common/Modal/Modal";
import { ConfirmationModal } from "../../../components/common/ConfirmationModal/ConfirmationModal";
import { Pagination } from "../../../components/common/Pagination/Pagination";
import { useShowScrollTop } from "../../../hooks/useShowScrollTop";
import { adminQuotationService } from "../../../services/adminQuotationService";
import { productService } from "../../../services/productService";
import { getErrorMessage } from "../../../utils/errors";
import type { ProductItem, QuotationItem } from "../../../types";
import "./QuotationsPage.css";

function QuotationsPage() {
  const { t, i18n } = useTranslation();
  const [quotations, setQuotations] = useState<QuotationItem[]>([]);

  const isArabic = i18n.resolvedLanguage?.startsWith("ar") ?? false;

  // Pagination & Loading State
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Search / Filter / Sort State
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [productOptions, setProductOptions] = useState<ProductItem[]>([]);

  // Detail Modal State
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState<QuotationItem | null>(null);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState<QuotationItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Floating Go to Top hook
  const { showScrollTop, scrollToTop } = useShowScrollTop(300);

  // Load products once for the filter dropdown
  useEffect(() => {
    productService
      .getProducts({ size: 100 })
      .then((response) => setProductOptions(response.content))
      .catch(() => setProductOptions([]));
  }, []);

  // Debounce the search box (700ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 700);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadQuotations = useCallback(
    async (pageToLoad: number) => {
      setIsFetching(true);
      setError(false);
      try {
        const response = await adminQuotationService.getAdminQuotations({
          search: debouncedSearch || undefined,
          productId: productFilter || undefined,
          sort,
          page: pageToLoad,
          size: 10,
        });
        setQuotations(response.content);
        setPage(pageToLoad);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
      } catch {
        setError(true);
      } finally {
        setIsFetching(false);
        setInitialLoading(false);
      }
    },
    [debouncedSearch, productFilter, sort]
  );

  // Re-fetch from page 0 whenever search / filter / sort changes
  useEffect(() => {
    loadQuotations(0);
  }, [loadQuotations]);

  const isSearching = searchTerm.trim() !== debouncedSearch;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString(i18n.resolvedLanguage, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const productNameOf = (quotation: QuotationItem) =>
    (isArabic
      ? quotation.productNameAr || quotation.productNameEn
      : quotation.productNameEn || quotation.productNameAr) || "—";

  const handleViewClick = (quotation: QuotationItem) => {
    setSelectedQuotation(quotation);
    setDetailModalOpen(true);
  };

  const handleDeleteClick = (quotation: QuotationItem) => {
    setQuotationToDelete(quotation);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!quotationToDelete || deleting) return;
    setDeleting(true);
    try {
      await adminQuotationService.deleteQuotation(quotationToDelete.id);
      toast.success(t("admin.quotationDeleted"));
      setDeleteModalOpen(false);
      setQuotationToDelete(null);
      // If we just removed the last row on a page beyond the first, step back one page
      loadQuotations(quotations.length === 1 && page > 0 ? page - 1 : page);
    } catch (deleteError) {
      toast.error(await getErrorMessage(deleteError, t("admin.deleteFailed")));
    } finally {
      setDeleting(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="admin-loading">
        <span className="auth-spinner auth-spinner-brand"></span>
      </div>
    );
  }

  if (error) {
    return <ConnectionError onRetry={() => loadQuotations(page)} />;
  }

  return (
    <div className="admin-quotations-page">
      {/* Page Header */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">{t("admin.quotations")}</h1>
          <p className="admin-page-subtitle">{t("admin.quotationsDesc")}</p>
        </div>
        <span className="admin-badge">
          {totalElements} {t("admin.totalQuotations")}
        </span>
      </div>

      {/* ─── Search & Filter Toolbar ─── */}
      <div className="svc-toolbar-wrap">
        <div className="svc-toolbar">
          <div className="svc-search-field">
            <span className="svc-search-field__icon">
              {isSearching ? (
                <span className="svc-search-spinner" />
              ) : (
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              )}
            </span>
            <input
              type="text"
              className="svc-search-field__input"
              placeholder={t("admin.searchQuotations")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
            />
            {searchTerm && (
              <button
                className="svc-search-field__clear"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
                type="button"
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <select
            className="prod-filter-select"
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            aria-label={t("admin.productFilter")}
          >
            <option value="">{t("admin.allProducts")}</option>
            {productOptions.map((product) => (
              <option key={product.id} value={product.id}>
                {isArabic ? product.nameAr : product.nameEn}
              </option>
            ))}
          </select>

          <select
            className="prod-filter-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label={t("admin.sortBy")}
          >
            <option value="newest">{t("admin.sortNewest")}</option>
            <option value="oldest">{t("admin.sortOldest")}</option>
            <option value="nameAsc">{t("admin.sortNameAsc")}</option>
            <option value="nameDesc">{t("admin.sortNameDesc")}</option>
          </select>
        </div>
        {/* Thin progress bar — visible only during silent re-fetches */}
        <div className={`svc-fetch-bar ${isFetching ? "svc-fetch-bar--active" : ""}`} />
      </div>

      {quotations.length === 0 ? (
        <EmptyState
          icon={
            <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          }
          title={t("admin.noQuotations")}
          description={debouncedSearch || productFilter ? t("admin.noSearchResultsDesc") : t("admin.noQuotationsDesc")}
        />
      ) : (
        <div className="admin-table-responsive quote-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t("admin.quotationDate")}</th>
                <th>{t("admin.quotationName")}</th>
                <th>{t("admin.quotationCompany")}</th>
                <th>{t("admin.quotationPhone")}</th>
                <th>{t("admin.quotationCity")}</th>
                <th>{t("admin.quotationEmail")}</th>
                <th>{t("admin.quotationProduct")}</th>
                <th>{t("admin.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {quotations.map((quotation) => (
                <tr key={quotation.id}>
                  <td>{formatDate(quotation.createdAt)}</td>
                  <td>{quotation.name}</td>
                  <td>{quotation.companyName || "—"}</td>
                  <td dir="ltr">{quotation.phone}</td>
                  <td>{quotation.city}</td>
                  <td>{quotation.email}</td>
                  <td>{productNameOf(quotation)}</td>
                  <td>
                    <div className="admin-action-btns">
                      <button
                        type="button"
                        className="admin-btn admin-btn-edit"
                        onClick={() => handleViewClick(quotation)}
                        aria-label={t("admin.viewQuotation")}
                      >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn-delete"
                        onClick={() => handleDeleteClick(quotation)}
                        aria-label={t("admin.delete")}
                      >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Numbered pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={loadQuotations}
        disabled={isFetching}
      />

      {/* Detail Modal */}
      <Modal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={t("admin.quotationDetails")}
        className="quote-detail-modal"
      >
        {selectedQuotation && (
          <div className="quote-detail">
            <div className="quote-detail__row">
              <span className="quote-detail__label">{t("admin.quotationDate")}</span>
              <span className="quote-detail__value">{formatDate(selectedQuotation.createdAt)}</span>
            </div>
            <div className="quote-detail__row">
              <span className="quote-detail__label">{t("admin.quotationName")}</span>
              <span className="quote-detail__value">{selectedQuotation.name}</span>
            </div>
            <div className="quote-detail__row">
              <span className="quote-detail__label">{t("admin.quotationCompany")}</span>
              <span className="quote-detail__value">{selectedQuotation.companyName || "—"}</span>
            </div>
            <div className="quote-detail__row">
              <span className="quote-detail__label">{t("admin.quotationPhone")}</span>
              <span className="quote-detail__value" dir="ltr">{selectedQuotation.phone}</span>
            </div>
            <div className="quote-detail__row">
              <span className="quote-detail__label">{t("admin.quotationCity")}</span>
              <span className="quote-detail__value">{selectedQuotation.city}</span>
            </div>
            <div className="quote-detail__row">
              <span className="quote-detail__label">{t("admin.quotationEmail")}</span>
              <span className="quote-detail__value">{selectedQuotation.email}</span>
            </div>
            <div className="quote-detail__row">
              <span className="quote-detail__label">{t("admin.quotationProduct")}</span>
              <span className="quote-detail__value">{productNameOf(selectedQuotation)}</span>
            </div>
            {selectedQuotation.description && (
              <div className="quote-detail__row quote-detail__row--block">
                <span className="quote-detail__label">{t("admin.quotationDescription")}</span>
                <p className="quote-detail__desc">{selectedQuotation.description}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        title={t("admin.deleteQuotationConfirmTitle")}
        message={t("admin.deleteQuotationConfirmDesc")}
        confirmLabel={t("admin.delete") || "Delete"}
        cancelLabel={t("admin.cancel") || "Cancel"}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
        type="danger"
      />

      {/* Floating scroll-to-top */}
      <button
        className={`admin-go-to-top ${showScrollTop ? "visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Go to top"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
    </div>
  );
}

export default QuotationsPage;
